import "../style.scss";

import { FormHelperText, MenuItem, Tooltip } from "@mui/material";
import { Info } from "assets/img/svg/info";
import InfoTooltip from "components/shared/InfoTooltip";
import { StyledSelect } from "components/shared/StyledSelect";
import { TinyBody } from "helpers/TypographyComponents";
import { forwardRef } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface SelectFormComponentProps {
    label?: string;
    placeholder?: string;
    items: { value: any; label: string }[];
    control: any;
    controlName: string;
    error?: any;
    required?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    sx?: any;
    labelStyle?: React.CSSProperties;
    handleChange?: (e: any) => void;
    /**
     * Used to render more complex option design.
     */
    renderItem?: (item: { value: any; label: string }) => JSX.Element;
    infoTooltip?: { text: string; link?: string; type: "info" | "question" };
    showInfoIcon?: boolean;
    showOptionalMark?: boolean;
    /**
     * Container style { width: "100%" }
     */
    fullWidth?: boolean;
}

const isEmptySelectValue = (selected: unknown) =>
    selected == null || selected === "" || (Array.isArray(selected) && selected.length === 0);

const labelForSelectValue = (items: SelectFormComponentProps["items"], value: unknown) =>
    items.find((item) => item.value === value)?.label ?? "";

const renderSelectValue = (selected: unknown, items: SelectFormComponentProps["items"], placeholder: string) => {
    if (isEmptySelectValue(selected)) {
        return <span className="select-placeholder">{placeholder}</span>;
    }
    if (Array.isArray(selected)) {
        return selected.map((value) => labelForSelectValue(items, value)).join(", ");
    }
    return labelForSelectValue(items, selected);
};

const formHelperText = {
    color: "#9B5067 !important",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontFamily: "Poppins",
    fontSize: "14px",
    paddingLeft: "12px",
    "&::before": {
        content:
            'url("data:image/svg+xml;utf8,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2015%2015%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20%3E%20%3Cpath%20d%3D%22M6.43148%202.41226L1.13773%2011.2498C1.02858%2011.4388%200.970831%2011.6531%200.97022%2011.8713C0.969609%2012.0896%201.02616%2012.3042%201.13424%2012.4938C1.24233%2012.6835%201.39818%2012.8415%201.58629%2012.9522C1.77441%2013.0629%201.98823%2013.1224%202.20648%2013.1248H12.794C13.0122%2013.1224%2013.226%2013.0629%2013.4142%2012.9522C13.6023%2012.8415%2013.7581%2012.6835%2013.8662%2012.4938C13.9743%2012.3042%2014.0308%2012.0896%2014.0302%2011.8713C14.0296%2011.6531%2013.9719%2011.4388%2013.8627%2011.2498L8.56898%202.41226C8.45756%202.22858%208.30068%202.07671%208.11347%201.97131C7.92627%201.86592%207.71506%201.81055%207.50023%201.81055C7.28539%201.81055%207.07418%201.86592%206.88698%201.97131C6.69978%202.07671%206.5429%202.22858%206.43148%202.41226V2.41226Z%22%20stroke%3D%22%239B5067%22%20stroke-width%3D%221.03448%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20%2F%3E%20%3Cpath%20d%3D%22M7.5%205.625V8.125%22%20stroke%3D%22%239B5067%22%20stroke-width%3D%221.03448%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20%2F%3E%20%3Cpath%20d%3D%22M7.5%2010.625H7.5066%22%20stroke%3D%22%239B5067%22%20stroke-width%3D%221.03448%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20%2F%3E%20%3C%2Fsvg%3E")',
        display: "flex",
    },
};

const SelectFormComponent = forwardRef<any, SelectFormComponentProps>(function SelectFormComponent(
    props: SelectFormComponentProps,
    ref,
) {
    const {
        label = "",
        placeholder,
        items,
        control,
        controlName,
        error,
        required,
        disabled = false,
        multiple,
        sx,
        labelStyle,
        handleChange,
        renderItem,
        infoTooltip,
        showOptionalMark,
        fullWidth = false,
    } = props;
    const [t] = useTranslation();

    return (
        <div className="form-component" style={fullWidth ? { width: "100%" } : {}}>
            <Controller
                name={controlName}
                control={control}
                rules={{
                    required: required ? t("NewElement.Required") : undefined,
                }}
                render={({ field }) => {
                    return (
                        <>
                            {label && (
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <label className="form-component-label" htmlFor={controlName} style={labelStyle}>
                                        {label}
                                        {required && "*"}
                                        {showOptionalMark && (
                                            <TinyBody color="grey">({t("NewElement.Optional")})</TinyBody>
                                        )}
                                    </label>
                                    {infoTooltip && (
                                        <InfoTooltip
                                            text={infoTooltip.text}
                                            iconType={infoTooltip.type}
                                            link={infoTooltip.link}
                                        />
                                    )}
                                </div>
                            )}
                            <StyledSelect
                                {...field}
                                error={!!error}
                                name={controlName}
                                fullWidth
                                displayEmpty={Boolean(placeholder)}
                                renderValue={
                                    placeholder
                                        ? (selected) => renderSelectValue(selected, items, placeholder)
                                        : undefined
                                }
                                disabled={disabled}
                                multiple={multiple}
                                sx={sx}
                                onSelect={handleChange}
                                ref={ref}
                            >
                                {items.map((item) => (
                                    <MenuItem key={`option-${item.value}`} value={item.value}>
                                        {renderItem ? renderItem(item) : item.label}
                                    </MenuItem>
                                ))}
                            </StyledSelect>
                            {!!error && (
                                <FormHelperText error={!!error} sx={formHelperText}>
                                    {error?.message ?? ""}
                                </FormHelperText>
                            )}
                        </>
                    );
                }}
            />
        </div>
    );
});

export default SelectFormComponent;
